import React, { useState } from 'react';
import { api } from '../api';
import { computePL, fmt } from '../utils';

export default function DealSetupSection({ deal, onUpdate }) {
  const [saving, setSaving] = useState(false);

  const save = async (field, value) => {
    setSaving(true);
    await api.updateDeal(deal.id, { [field]: value === '' ? null : value });
    setSaving(false);
    onUpdate();
  };

  const saveMulti = async (fields) => {
    setSaving(true);
    await api.updateDeal(deal.id, fields);
    setSaving(false);
    onUpdate();
  };

  const apply3Shacks = () => {
    saveMulti({
      hm_lender: '3 Shacks',
      hm_points: 2,
      hm_rate: 12,
      hm_insurance: 2500,
    });
  };

  // Live loan calculation for display
  const pl = computePL(deal);

  const Field = ({ label, field, type = 'text', placeholder, span }) => (
    <div className={span ? 'span-2' : ''}>
      <label>{label}</label>
      <input
        type={type}
        defaultValue={deal[field] ?? ''}
        placeholder={placeholder}
        onBlur={e => {
          const v = type === 'number' ? (e.target.value === '' ? null : parseFloat(e.target.value)) : e.target.value;
          if (String(v) !== String(deal[field] ?? '')) save(field, v);
        }}
      />
    </div>
  );

  return (
    <div className="card deal-section">
      <div className="section-title">Deal Setup {saving && <span style={{fontSize:11,color:'var(--text-muted)'}}>saving…</span>}</div>

      <div className="form-grid" style={{marginBottom:16}}>
        <Field label="Address" field="address" span placeholder="123 Main St" />
        <Field label="ARV ($)"            field="arv"            type="number" placeholder="250000" />
        <Field label="Purchase Price ($)" field="purchase_price" type="number" placeholder="130000" />
        <Field label="City"    field="city" placeholder="Jacksonville" />
        <Field label="Zip"     field="zip" />
        <Field label="Sq Footage" field="sq_footage" type="number" />
        <Field label="Bedrooms"   field="bedrooms"   type="number" />
        <Field label="Bathrooms"  field="bathrooms"  type="number" />

        <div>
          <label>Status</label>
          <select defaultValue={deal.status} onBlur={e => save('status', e.target.value)}>
            {['Pre-Deal','Purchased','On Market','Under Contract','Sold'].map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <Field label="Partner name" field="partner_name" placeholder="3 Shacks LLC" />
        <Field label="Purchase / Closing Date" field="purchase_date"       type="date" />
        <Field label="Expected Sale Date"      field="expected_close_date" type="date" />
        <Field label="Hold Period (months)"    field="hold_months"         type="number" />
        <Field label="Actual Sale Price ($)"   field="actual_sale_price"   type="number" placeholder="Leave blank until sold" />
      </div>

      {/* ── Hard Money ── */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
        <div className="section-title" style={{marginBottom:0}}>Hard Money</div>
        <button
          className="btn-secondary"
          style={{fontSize:11,padding:'4px 10px'}}
          onClick={apply3Shacks}
          title="Set lender to 3 Shacks: 2 points, 12%/yr, $2,500 insurance"
        >
          ⚡ 3 Shacks Defaults
        </button>
      </div>
      <div className="form-grid">
        <Field label="Lender"          field="hm_lender" />
        <Field label="Rate (%/year)"   field="hm_rate"       type="number" placeholder="12" />
        <Field label="Points (%)"      field="hm_points"     type="number" placeholder="2" />
        <Field label="Insurance ($)"   field="hm_insurance"  type="number" placeholder="2500" />
      </div>

      {/* Loan calculation display */}
      {(deal.purchase_price > 0 || deal.arv > 0) && (
        <div style={{background:'var(--bg)',borderRadius:'var(--radius)',padding:'10px 14px',
          marginTop:10,fontSize:12}}>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
            <span style={{color:'var(--text-muted)'}}>Calculated Loan (90% PP + Rehab)</span>
            <span style={{fontWeight:700}}>{fmt(pl.loan)}</span>
          </div>
          <div style={{display:'flex',justifyContent:'space-between'}}>
            <span style={{color:'var(--text-muted)'}}>75% ARV Check (max loan)</span>
            <span style={{fontWeight:600, color: deal.arv > 0 ? (pl.arvQualOk ? 'var(--success)' : 'var(--danger)') : 'var(--text-muted)'}}>
              {deal.arv > 0
                ? (pl.arvQualOk ? `✓ OK — max ${fmt(pl.arvCheck75)}` : `⚠ Exceeds — max ${fmt(pl.arvCheck75)}`)
                : '— enter ARV'}
            </span>
          </div>
        </div>
      )}

      <div className="section-title" style={{marginTop:16}}>Deal Percentages</div>
      <div className="form-grid cols-3" style={{maxWidth:480}}>
        <Field label="Profit Target %" field="profit_pct"            type="number" />
        <Field label="Back-End %"      field="backend_pct"           type="number" />
        <Field label="Front-End %"     field="frontend_pct"          type="number" />
        <Field label="Holding %/month" field="holding_pct_per_month" type="number" />
        <Field label="Contingency %"   field="contingency_pct"       type="number" />
      </div>

      <div className="section-title" style={{marginTop:16}}>Rehab Budget</div>
      <div className="form-grid" style={{maxWidth:320}}>
        <Field label="Rehab Budget ($) — manual override" field="rehab_budget_manual" type="number" placeholder="Overrides SOW total" />
      </div>
    </div>
  );
}
